// © Microsoft Corporation. All rights reserved.

import { AccountType, AppType, AudienceModeType, AuthCookieName, PageType } from "@msnews/experiences-constants";
import { AppErrorPropertyBag, getAppErrorsPropertyBag, getPageTimings, updateAppErrorsPropertyBag } from "@msnews/diagnostics";

import {
    appAnonCookieExistsCookieName,
    childCookieName,
    rootElementId,
    serviceUrl,
    telemetryId,
    trackPageProduct,
    trackPageType,
    EnterpriseToConsumerOverrideKey
} from "../constants";

import { Environment, getEnvironmentFromString } from "./Environment";
import { HostPage, IHostPage } from "./HostPage";
import { WpoModel, getWpoFlights } from "@msnews/wpo-common";
import { canUseDOM, getDocumentElementById, getLocationHostname, getLocationHref, getLocationOrigin, getLocationSearch } from "@msnews/isomorphic-util";
import { getCookie, getMuidCookie } from "../utilities/CookieUtility";
import { getLocalStorage } from "../webstorage/LocalStorage";

import { AccountInfo } from "./Enterprise";
import { Market } from "./Market";
import { PlatformType, PageRequestTargetScope } from "@msnews/config-data-types";
import { UrlUtility } from "../utilities/UrlUtility";
import { Utility } from "../utilities/Utility";
import { headData } from "./HeadData";
import { isString } from "lodash-es";
import { tryParseJson } from "../utilities/Parsing";
import { shouldUseGitConfigs } from "@msnews/experimentation-helpers";
import { ConfigCacheProperties, ConfigOrigin, ConfigOrigins } from "@msnews/core-contracts";
import { updateResolverAppErrorsPropertyBag } from "@msnews/config-resolver";
import { isWindowsScenario } from "@msnews/url-util";

/**
 * chrome
 * The chrome API is not defined in Typescript
 */
declare global {
    interface Window { chrome: any; }
}

/**
 * Page level experience tracking info
 */
export interface ITrackInfo {
    sitePage: {
        page_product: string,
        page_type: string
    }
}

// This is defined in webpack config
declare let __WEBPACK_LOCAL_GITCONFIGS__: string;
declare let __WEBPACK_LOCAL_GITCONFIGS_LOC__: string;

export class AppEnvironment {
    /**
     * The single instance of the AppEnvironment class.
     * @type {AppEnvironment}
     */
    private static _instance: AppEnvironment;

    /**
     * the activity id for the current page view
     */
    private activityId: string;

    /**
     * Base url for the content service, endpoints/queries are appended for specific content calls.
     * This is an akamai url for highly cacheable content.
     * Possible values are enumerated on ContentServiceConfig.ts
     *
     * @private
     * @type {string}
     * @memberof AppEnvironment
     */
    private contentServiceUrlBase: URL;

    private currentRequestTargetScope: PageRequestTargetScope;

    private currentMarket: Market;
    private hostPage: IHostPage;

    /** The hostname to use for content links. */
    private currentContentLinkHost: string;

    /**
     * Indicates that the page is running as an FRE page (i.e. the url contains fre=1)
     * Does not indicate that the FRE itself is currently displayed over the page.
     */
    private isFrePage: boolean;

    /** Flag to enable five column layout. */
    private enableFiveColLayout: boolean;

    /**
     * Base for the service url, endpoints/queries are appended for specific service calls.
     * This is the OneService url used for personalized content.
     * Possible values are enumerated on OneServiceConfig.ts
     *
     * @private
     * @type {string}
     * @memberof AppEnvironment
     */
    private serviceUrlBase: URL;

    /**
     * Base URL for the feed service
     */
    private feedServiceUrlBase: URL;

    /**
     * Base domain for the feed service
     */
    private serviceDomainUrl: string;

    /**
     * Base domain host for static resources
     */
    private staticDomain: string;

    /**
     * Base domain for the enterprise feed service
     */
    private enterpriseServiceDomainUrl: string;

    /**
     * The account info for the current page user wrapped in a promise
     */
    private accountInfoPromise: Promise<AccountInfo>;

    /**
     * The resolved account info. This is only set when the account info promise is resolved.
     */
    private resolvedAccountInfo: AccountInfo;

    private appType: string;
    private authCookieName: AuthCookieName;
    private buildVersion: string;

    private environmentConfigUrl: URL;

    private rootTelemetryId: number;
    private trackInfo: ITrackInfo;
    private userId: string;
    private countryCode: string;
    private latitude: string;
    private longitude: string;
    private environment: Environment;
    private featureFlags: any;
    private disableCachingConfigs: boolean;
    private enableConfigService: boolean;
    private isMultiConfigRetrievalEnabled: boolean;
    private configServiceBaseUrl: URL;
    private disableContentPreviewCache: boolean;
    private disableContentCacheIDBRead: boolean;
    private sendFeedCallActivityIdInHeader: boolean;
    private useOneServiceAuthEndpoints: boolean;
    private useOneServiceAnonAuthEndpoints: boolean;
    private useAuthFetch: boolean;
    private clarityEnabled: boolean;
    private isCnDomain: boolean;
    private oneServiceApiKey: string;

    /** Flag specific to windows-shell. True if User is AAD Account Type */
    private isWinAADUser: boolean;

    /** True if query string has appropriate debug flag */
    private isDebug: boolean;

    /** True if query string has appropriate flag to show msnCreatorDashboard (msnDash=<any>) */
    private showMnsCreatorDashboard: boolean;

    /** Config Root Url */
    private configRootUrl: URL;

    /** Cache busting identifier received from pcs */
    private cacheBustingIdentifier: string;

    /** True if this is git configs flight */
    private isGitConfigs: boolean;

    /** Indicates whether feature/localization configs are fetched from local repo */
    private configOrigins: ConfigOrigins;

    /** For Config caching optimizations. */
    private configCacheProperties: ConfigCacheProperties;

    /** CRS endpoint version. Example: v2 or v3 */
    private crsVersion: string;

    /** CRS endpoint domain. Example: https://www.msn.com */
    private crsDomain: string;

    /** Root config index CMS Id. Example: cms/api/amp/experienceConfigIndex/BBUsYQa */
    private configIndexDocId: string;

    /** The WPOData we get from local storage using the requestContext wpoData function */
    private wpoData: string;

    /** Taboola cookie value */
    private ecasession: string;

    /** is browser optout cookie equals 1 */
    private browserOptOut: boolean;

    public get Ecasession(): string {
        if (this.ecasession === undefined) {
            this.ecasession = getCookie("ecasession") || "init";
        }
        return this.ecasession;
    }

    public get BrowserOptOut(): boolean {
        if (this.browserOptOut === undefined) {
            this.browserOptOut = getCookie("TOptOut") === "1";
        }
        return this.browserOptOut;
    }

    public get WpoData(): string { return this.wpoData; }
    public set WpoData(value: string) {
        this.wpoData = value;
        this.addWpoFlights();
    }

    /**
     * ActivityId value from initial server response, use telemetry's requestId instead for associating with current page client navigation
     */
    public get ActivityId(): string { return this.activityId; }
    public set ActivityId(value: string) {
        this.activityId = value;
        headData.ClientSettings = {
            ...headData.ClientSettings,
            aid: value
        };
    }

    /**
     * Getter for the base url for the content service, endpoints/queries are appended for specific content calls.
     * This is an akamai url for highly cacheable content.
     * Possible values are enumerated on ContentServiceConfig.ts
     *
     * @readonly
     * @type {URL}
     * @memberof AppEnvironment
     */
    public get EnvironmentConfigUrl(): URL {
        // If I mutate the same object, I will need to revert to the original state somewhere.
        // For safety reason, it is better to use a new Object
        const url = new URL("", this.environmentConfigUrl);

        if (process.env.WEBPACK_DEV_SERVER || new URLSearchParams(getLocationSearch()).get("env") === "dev") {
            url.searchParams.set("env.config", "dev");
        }

        const searchParam = new URLSearchParams(getLocationSearch()).get("env.config");
        if (searchParam) {
            url.searchParams.set("env.config", searchParam);
        }

        return url;
    }

    /**
     * Getter for the base url for the content service, endpoints/queries are appended for specific content calls.
     * This is an akamai url for highly cacheable content.
     * Possible values are enumerated on ContentServiceConfig.ts
     *
     * @readonly
     * @type {URL}
     * @memberof AppEnvironment
     */
    public get ContentServiceUrlBase(): URL {
        // If I mutate the same object, I will need to revert to the original state somewhere.
        // For safety reason, it is better to use a new Object
        const url = new URL("", this.contentServiceUrlBase);

        if (process.env.WEBPACK_DEV_SERVER || new URLSearchParams(getLocationSearch()).get("env") === "dev") {
            url.searchParams.set("env.content", "dev");
        }

        const searchParam = new URLSearchParams(getLocationSearch()).get("env.content");
        if (searchParam) {
            url.searchParams.set("env.content", searchParam);
        }

        return url;
    }

    public get CurrentRequestTargetScope(): PageRequestTargetScope { return this.currentRequestTargetScope; }
    public set CurrentRequestTargetScope(value: PageRequestTargetScope) { this.currentRequestTargetScope = value; }

    public get CurrentMarket(): Market { return this.currentMarket; }
    public set CurrentMarket(value: Market) { this.currentMarket = value; }

    public get EnableFiveColLayout(): boolean { return this.enableFiveColLayout; }
    public set EnableFiveColLayout(value: boolean) { this.enableFiveColLayout = value; }

    public get Environment(): Environment { return this.environment; }
    public set Environment(value: Environment) { this.environment = value; }

    public get StaticDomain(): string { return this.staticDomain; }

    public get HostPage(): IHostPage { return this.hostPage; }
    public set HostPage(value: IHostPage) { this.hostPage = value; }

    public get AppType(): string { return this.appType; }

    public get AuthCookieName(): AuthCookieName { return this.authCookieName; }
    public set AuthCookieName(value: AuthCookieName) { this.authCookieName = value; }

    public get UseOneServiceAuthEndpoints(): boolean { return this.useOneServiceAuthEndpoints; }
    public set UseOneServiceAuthEndpoints(value: boolean) { this.useOneServiceAuthEndpoints = value; }

    public get UseOneServiceAnonAuthEndpoints(): boolean { return this.useOneServiceAnonAuthEndpoints; }
    public set UseOneServiceAnonAuthEndpoints(value: boolean) { this.useOneServiceAnonAuthEndpoints = value; }

    public get UseAuthFetch(): boolean { return this.useAuthFetch; }
    public set UseAuthFetch(value: boolean) { this.useAuthFetch = value; }

    public get AccountInfoPromise(): Promise<AccountInfo> { return this.accountInfoPromise; }
    public set AccountInfoPromise(value: Promise<AccountInfo>) { this.accountInfoPromise = value; }

    public get ResolvedAccountInfo(): AccountInfo { return this.resolvedAccountInfo; }
    public set ResolvedAccountInfo(value: AccountInfo) { this.resolvedAccountInfo = value; }

    public get BuildVersion(): string { return this.buildVersion || ""; }

    public get ClarityEnabled(): boolean { return this.clarityEnabled; }
    public set ClarityEnabled(value: boolean) { this.clarityEnabled = value; }

    public get IsWinAADUser(): boolean { return this.isWinAADUser; }
    public set IsWinAADUser(value: boolean) { this.isWinAADUser = value; }

    public get RootTelemetryId(): number { return this.rootTelemetryId; }
    public set RootTelemetryId(value: number) { this.rootTelemetryId = value; }

    public get TrackInfo(): ITrackInfo { return this.trackInfo; }
    public set TrackInfo(value: ITrackInfo) { this.trackInfo = value; }

    public get UserId(): string { return this.getUserMuid() || this.userId; }
    public set UserId(value: string) { this.userId = value; }

    public get OneServiceApiKey(): string { return this.oneServiceApiKey; }
    public set OneServiceApiKey(value: string) { this.oneServiceApiKey = value; }

    /** True if query string has appropriate debug flag */
    public get IsDebug(): boolean { return this.isDebug; }

    /** True if query string has appropriate flag to show msnCreatorDashboard (msnDash=<any>) */
    public get ShowMnsCreatorDashboard(): boolean { return this.showMnsCreatorDashboard; }

    /**
     * Gets the host to use when linking to content.
     */
    public get contentLinkHost(): string { return this.currentContentLinkHost; }

    public get IsCnDomain(): boolean { return this.isCnDomain; }

    /**
     * Getter for the base for the service url, endpoints/queries are appended for specific service calls.
     * This is the oneservice url used for personalized content.
     * Possible values are enumerated on OneServiceConfig.ts
     *
     * @type {URL}
     * @memberof AppEnvironment
     */
    public get ServiceUrlBase(): URL {
        const url = new URL("", this.serviceUrlBase);

        if (process.env.WEBPACK_DEV_SERVER || new URLSearchParams(getLocationSearch()).get("env") === "dev") {
            url.searchParams.set("env.service", "dev");
        }

        const searchParam = new URLSearchParams(getLocationSearch()).get("env.service");
        if (searchParam) {
            url.searchParams.set("env.service", searchParam);
        }

        return url;
    }

    /**
     * Base URL for the feed service
     */
    public get FeedServiceUrlBase(): URL {
        if (!this.feedServiceUrlBase) {
            return null;
        }

        const url = new URL("", this.feedServiceUrlBase);

        if (process.env.WEBPACK_DEV_SERVER || new URLSearchParams(getLocationSearch()).get("env") === "dev") {
            url.searchParams.set("env.service", "dev");
        }

        const searchParam = new URLSearchParams(getLocationSearch()).get("env.service");
        if (searchParam) {
            url.searchParams.set("env.service", searchParam);
        }

        return url;
    }

    /**
     * Base URL for the enterprise service
     */
    public get EnterpriseServiceUrlBase(): URL {
        if (!this.enterpriseServiceDomainUrl) {
            return;
        }

        const url = new URL("", this.enterpriseServiceDomainUrl);

        if (process.env.WEBPACK_DEV_SERVER || new URLSearchParams(getLocationSearch()).get("env") === "dev") {
            url.searchParams.set("env.service", "dev");
        }

        const searchParam = new URLSearchParams(getLocationSearch()).get("env.service");
        if (searchParam) {
            url.searchParams.set("env.service", searchParam);
        }

        return url;
    }

    /**
     * Returns true if audience mode is set to Enterprise and the user is logged into an AAD account, else false
     */
    public get isAadAccountUser(): boolean {
        return this.CurrentRequestTargetScope && this.CurrentRequestTargetScope.audienceMode === AudienceModeType.Enterprise &&
            this.ResolvedAccountInfo && this.ResolvedAccountInfo.account_type && this.ResolvedAccountInfo.account_type === AccountType.AAD;
    }

    public get IsFrePage(): boolean { return this.isFrePage; }

    /**
     * This is the switch to turn on/off indexed db operation used as a caching layer in config resolver (Resolver.ts)
     */
    public get DisableCachingConfigs(): boolean { return this.disableCachingConfigs; }

    /**
     * This is the switch to turn on/off config service operation
     */
    public get EnableConfigService(): boolean {
        const queryParamOverride = UrlUtility.getQueryParameterByName("enableConfigService", getLocationSearch());

        if (queryParamOverride) {
            return queryParamOverride.toLocaleLowerCase() === "true";
        }
        return this.enableConfigService;
    }

    /**
     * This indicates whether we will use CMS PPE configs or not
     */
    public get EnableCMSPPEConfigs(): boolean { return headData && headData.EnableCMSPPEConfigs; }

    /**
     * Check whether multi config retrieval is supported.
     */
    public get IsMultiConfigRetrievalEnabled(): boolean { return this.isMultiConfigRetrievalEnabled; }

    /**
     * This is the switch to turn on/off content preview caching in core/cache/ContentPreview.ts
     */
    public get DisableContentPreviewCache(): boolean {
        return this.disableContentPreviewCache;
    }

    /**
     * Returns a flag indicating whether the one service feed fetch call should send activityID
     * in the request header or as a QSP in the URL (current behavior is via QSP).
     */
    public get SendFeedCallActivityIdInHeader(): boolean {
        return this.sendFeedCallActivityIdInHeader;
    }

    /**
     * This is the switch to turn off hydrating content preview cache from IndexedDB.
     * Default is false
     */
    public get DisableContentCacheIDBRead(): boolean {
        const queryParamOverride = UrlUtility.getQueryParameterByName("disableContentCacheIDBRead", getLocationSearch());

        if (queryParamOverride) {
            return queryParamOverride === "true";
        }

        return this.disableContentCacheIDBRead;
    }

    /**
     * Get base url of Config Service.
     */
    public get ConfigServiceBaseUrl(): URL { return this.configServiceBaseUrl; }

    /**
     * This returns feature flags client settings stamped by PCS.
     */
    public get FeatureFlags(): any { return this.featureFlags; }

    /**
     * This returns config root url
     */
    public get ConfigRootUrl(): URL {
        if (!this.configRootUrl) {
            return null;
        }

        const url = new URL("", this.configRootUrl);

        if (new URLSearchParams(getLocationSearch()).get("env") === "dev") {
            url.searchParams.set("env.config", "dev");
        }

        const searchParam = new URLSearchParams(getLocationSearch()).get("env.config");
        if (searchParam) {
            url.searchParams.set("env.config", searchParam);
        }

        return url;
    }

    /**
     * Returns cache busting identifier from PCS.
     */
    public get CacheBustingIdentifier(): string { return this.cacheBustingIdentifier; }

    /**
     * True if this is git configs flight
     */
    public get IsGitConfigs(): boolean { return this.isGitConfigs; }

    /**
     * Returns the country code. For example "br"
     */
    public get CountryCode(): string { return this.countryCode; }
    public set CountryCode(value: string) { this.countryCode = value; }

    /// / FIXME: hyphen has a second 'h'
    /// / Bug 24228723: [Peregrine] ActivityIdLowerCaseNoHypens misspells "Hyphen"
    /**
     * Gets the Activity ID in lower case and without any hyphens. This helper method should be where parity with Prime is required.
     * Use telemetry's requestId instead for associating with current page client navigation
     */
    public get ActivityIdLowerCaseNoHypens(): string { return ((headData && headData.ClientSettings && headData.ClientSettings.aid) || "").toLowerCase(); }

    /**
     * Returns the latitude. For example "42.361145"
     */
    public get Latitude(): string { return this.latitude; }

    /**
     * Returns the longitude. For example "-71.057083"
     */
    public get Longitude(): string { return this.longitude; }

    /** For Config caching optimizations. */
    public get ConfigCacheProperties(): ConfigCacheProperties { return this.configCacheProperties; }

    /** Fetches local dev environment for client resolver */
    public get ConfigOrigins(): ConfigOrigins { return this.configOrigins; }

    /**
     * Indicates whether configs should be fetched from local repo based on conditions and overrides
     */
    private setConfigOrigin(): ConfigOrigins {
        // Default to production
        const configOrigins: ConfigOrigins = { feature: ConfigOrigin.Production, loc: ConfigOrigin.Production };

        if (__WEBPACK_LOCAL_GITCONFIGS_LOC__) {
            configOrigins.feature = ConfigOrigin.Local;
            configOrigins.loc = ConfigOrigin.Local;
        } else if (__WEBPACK_LOCAL_GITCONFIGS__) {
            configOrigins.feature = ConfigOrigin.Local;
            configOrigins.loc = ConfigOrigin.Production;
        }

        // Override based on conditions
        if (!this.IsGitConfigs || this.EnableConfigService) {
            configOrigins.feature = ConfigOrigin.Production;
            configOrigins.loc = ConfigOrigin.Production;
        }
        return configOrigins;
    }

    /**
     * Initialize the AppEnvironment singleton instance.
     * @param _serviceUrl Optional URL to initialize the ServiceUrlBase.
     * Render service can be deployed to the shared cluster or OneService cluster. Depending on where it is deployed, we can override the configServiceUrl or serviceDomainUrl to speed up the performance
     * @param _configServiceOverride When render service is deployed to the shared cluster. We want to make calls to the config service within the shared cluster region, instead of calling www.msn.com
     * @param _serviceDomainOverride When render service is deployed to OneService cluster, we want to make calls to the feed service within the OneService cluster region, instead of calling assets.msn.com
     */
    private constructor(_serviceUrl: URL, _configServiceOverride: string, _serviceDomainOverride: string) {
        this.initializeAppEnvironment(_serviceUrl, _configServiceOverride, _serviceDomainOverride);
    }

    /**
     * Returns singleton class instance.
     * @returns {AppEnvironment}
     */
    public static getInstance(serviceUrl: URL = null, configServiceOverride: string = null, serviceDomainOverride: string = null): AppEnvironment {
        return AppEnvironment._instance || (AppEnvironment._instance = new AppEnvironment(serviceUrl, configServiceOverride, serviceDomainOverride));
    }

    /**
     * Reinitialize the singleton class instance.
     * @param serviceUrl OneService service url to initialize to.
     * @param configServiceOverride url that is used to override config service url.
     * @param serviceDomainOverride url that is used to override service domain url.
     * This value is externally configured from SSR environment
     * @returns {AppEnvironment}
     */
    public static resetInstance(serviceUrl: URL = null, configServiceOverride: string = null, serviceDomainOverride: string = null): AppEnvironment {
        AppEnvironment._instance = new AppEnvironment(serviceUrl, configServiceOverride, serviceDomainOverride);
        appEnvironment = AppEnvironment._instance;
        return AppEnvironment._instance;
    }

    /**
     * Initializes App environment. This is normally only called by the constructor (singleton pattern)
     * It is made public so that it can be also called by test code.
     */
    public initializeAppEnvironment(_serviceUrl?: URL, configServiceOverride?: string, serviceDomainOverride?: string): void {

        let environmentFromHeadData: Environment = null;
        const clientSettings = headData && headData.ClientSettings;

        const locationHost: string = getLocationHostname();

        if (clientSettings) {
            // Use Cn domain if page locationHost ends with .cn or client settings has explicit flag to use Cn domain
            // useCnDomain is not sent by PCS.
            // useCnDomain is added by widget initializer, that is set by host page in Peregrine Widgets architecture.
            this.isCnDomain = locationHost.toLowerCase().endsWith(".cn") || clientSettings.useCnDomain || false;

            this.activityId = this.tryReformatGuid(clientSettings.aid);
            this.currentMarket = (headData.Locale || clientSettings.apps_locale) as Market;
            this.countryCode = clientSettings.geo_country;
            this.latitude = clientSettings.geo_lat;
            this.longitude = clientSettings.geo_long;
            this.configRootUrl = clientSettings.configRootUrl ? new URL("", clientSettings.configRootUrl) : null;
            environmentFromHeadData = getEnvironmentFromString(clientSettings.pcsInfo && clientSettings.pcsInfo.env || "prod");

            this.setAccountInfoPromise();
            this.appType = clientSettings.apptype || "";
            this.authCookieName = this.getAuthCookieName();
            this.useOneServiceAuthEndpoints = false;
            this.useOneServiceAnonAuthEndpoints = false;
            this.useAuthFetch = false;

            this.featureFlags = clientSettings.featureFlags;

            this.disableCachingConfigs =
                this.featureFlags && this.featureFlags.disableCachingConfigs
                    ? this.featureFlags.disableCachingConfigs === "true"
                    : false;

            this.enableConfigService =
                this.featureFlags && this.featureFlags.enableConfigService
                    ? this.featureFlags.enableConfigService === "true"
                    : true;

            this.disableContentPreviewCache =
                this.featureFlags && this.featureFlags.disableContentPreviewCache
                    ? this.featureFlags.disableContentPreviewCache === "true"
                    : false;

            this.disableContentCacheIDBRead = this.featureFlags && this.featureFlags.disableContentCacheIDBRead
                ? this.featureFlags.disableContentCacheIDBRead === "true"
                : false;

            // Use feature flag as a switch to disable the feature (will need a PCS deployment)
            this.sendFeedCallActivityIdInHeader = this.featureFlags && this.featureFlags.disableActivityIdInHeader && this.featureFlags.disableActivityIdInHeader === "true"
                ? false
                : this.appType === AppType.Edge || this.appType === AppType.Hub;

            this.cacheBustingIdentifier = clientSettings.cbid;

            const runtimePropertyBagInfo: AppErrorPropertyBag = {};

            // Capture PCS version info
            if (clientSettings.pcsInfo && !getAppErrorsPropertyBag().pcsVersion) {
                runtimePropertyBagInfo.pcsVersion = clientSettings.pcsInfo.v;
            }

            // Capture app type info
            runtimePropertyBagInfo.appType = this.appType;

            const bundleInfo = clientSettings.bundleInfo;

            if (bundleInfo) {
                const { alias, configsHash, hash, locConfigsHash, v } = bundleInfo;
                this.buildVersion = v || "";

                // Capture build version info
                runtimePropertyBagInfo.build = this.buildVersion;

                const buildAlias = alias;
                if (buildAlias) {
                    // Capture build alias info
                    runtimePropertyBagInfo.buildAlias = buildAlias;
                }

                if (hash && configsHash && locConfigsHash) {
                    this.configCacheProperties = {
                        bundleHash: hash,
                        configHash: configsHash,
                        locHash: locConfigsHash
                    };
                }
            }

            // Update base property bag defined in the each AppErrors module file
            updateAppErrorsPropertyBag(runtimePropertyBagInfo);
            updateResolverAppErrorsPropertyBag(runtimePropertyBagInfo);

            // Get configIndexDocId
            this.configIndexDocId = clientSettings.configIndexDocId;
        }

        const rootElement = canUseDOM() ? getDocumentElementById(rootElementId) : null;
        const locationHref = getLocationHref();
        const locationOrigin = getLocationOrigin();

        // env is read from
        //  - query string param env, if present
        //  - headData.ClientSettings.pcsInfo.env
        //  - defaults to prod otherwise
        const envQSOverwrite = getEnvironmentFromString(UrlUtility.getQueryParameterByName("env", locationHref));
        this.environment =
            envQSOverwrite
            || environmentFromHeadData
            || Environment.prod;

        const servicesEndpoints = clientSettings && clientSettings.servicesEndpoints;
        const pcsProvidedDomainUrl = servicesEndpoints && servicesEndpoints.feedService && servicesEndpoints.feedService.domain || locationOrigin;

        if (canUseDOM()) {
            // Get from headdata if possible.
            // Else using locationOrigin is okay (current MMX scenario) since it'd use .cn version for China.
            // todo: pass this to SW to override regex in service-worker/src/swConstants/DefaultRoutes.enum.ts
            this.serviceDomainUrl = pcsProvidedDomainUrl;
        } else {
            this.serviceDomainUrl = serviceDomainOverride || pcsProvidedDomainUrl;
        }

        this.staticDomain = new URL(pcsProvidedDomainUrl).host;
        this.enterpriseServiceDomainUrl = servicesEndpoints && servicesEndpoints.enterpriseFeedService && servicesEndpoints.enterpriseFeedService.domain;

        // Get CRS endpoint version. Example: v2 or v3 and domain given by PCS
        this.crsVersion = servicesEndpoints && servicesEndpoints.crs && servicesEndpoints.crs.v;
        this.crsDomain = servicesEndpoints && servicesEndpoints.crs && servicesEndpoints.crs.domain;

        this.serviceUrlBase = _serviceUrl || (rootElement && rootElement.getAttribute(serviceUrl) && new URL("", rootElement.getAttribute(serviceUrl))) || new URL("/service/", this.serviceDomainUrl);
        this.feedServiceUrlBase = this.serviceDomainUrl ? new URL("/service/", this.serviceDomainUrl) : undefined;

        // Get the hostPage from the request url
        this.hostPage = HostPage.getHostPageFromUrl(locationHref);

        // Determine if the page is running as an FRE page
        this.isFrePage = (UrlUtility.getQueryParameterByName("fre", locationHref) === "1");

        this.userId = this.getUserMuid();

        // Get attributes from the head (full page Peregrine experience) or from the spa "root" element (PrimeHybrid case)
        // TODO, determine where in the <head> element attributes these values should be set
        this.rootTelemetryId = rootElement ? Number(rootElement.getAttribute(telemetryId)) : 0;
        const trackPageProd = rootElement && rootElement.getAttribute(trackPageProduct);
        const trackPageTypeVal = rootElement && rootElement.getAttribute(trackPageType);
        this.trackInfo = { sitePage: { page_product: trackPageProd, page_type: trackPageTypeVal } };

        this.currentContentLinkHost = this.createContentLinkHost();

        this.setTargetScope();

        // Before calling this function make sure that
        // 1) Target scope is set and pageExperiments are available. No flights is fine.
        // 2) ServiceEndPoints are set and crsVersion is available.
        // 3) configIndexDocId is set.
        // 4) appType is set correctly.
        this.isGitConfigs = this.useGitConfigs();

        // Before calling this function make sure that
        // 1) Target scope is set and pageExperiments are available. No flights is fine.
        // 2) configIndexDocId is set.
        // 3) appType is set correctly.
        // 4) locationHref is set
        // 5) isGitConfigs is set
        this.addHubWindowsFlight(locationHref);
        this.configOrigins = this.setConfigOrigin();

        // Temporarily hardcode endpoint for MMX flight, when flight is off, we want to use hardcoded value.
        // After the flight, we hope to start using the regular logic for MMX
        if (this.appType === AppType.MMX && headData && !headData.IsMmxWwwContentRequest) {
            const mmxExperimentContentHost = getLocationHostname().endsWith(".cn") ? "https://assets.msn.cn" : "https://assets.msn.com";
            this.contentServiceUrlBase = new URL("/content/", mmxExperimentContentHost);
        } else {
            // IsMmxWwwContentRequest still comes here
            this.contentServiceUrlBase = this.serviceDomainUrl ? new URL("/content/", this.serviceDomainUrl) : undefined;
        }

        this.isMultiConfigRetrievalEnabled = this.useMultiConfigsRetrieval();

        // Make sure that this.isGitConfigs is set before calling getConfigServiceBaseUrl.
        // Make sure that isCnDomain is set before calling this function
        const baseUrl = this.getConfigServiceBaseUrl(this.isMultiConfigRetrievalEnabled, configServiceOverride);

        this.configServiceBaseUrl = baseUrl ? new URL("", baseUrl) : null;

        this.environmentConfigUrl = this.serviceDomainUrl ? new URL("/config/v1/", this.serviceDomainUrl) : undefined;

        this.isDebug = ((UrlUtility.getQueryParameterByName("debug", locationHref) || "").toLowerCase() in { 1: 1, true: 1 })
            || (UrlUtility.getQueryParameterByName("item", locationHref) || "").toLowerCase().indexOf("/debug") > 0
            || locationHost.includes("localhost");

        this.showMnsCreatorDashboard = UrlUtility.getQueryParameterByName("mnsDash", locationHref) != null;

        this.oneServiceApiKey = this.getOneServiceApiKey();

        this.enableFiveColLayout = false;
    }

    /**
     * Get the ApiKey that should be used by the current AppType
     * Please work OneService (aka: IDX) before updating it as this can break features
     * even if the call succeeds.
     */
    private getOneServiceApiKey(): string {
        let apiKey;
        switch (this.appType) {
            case AppType.WindowsShell:
                apiKey = "pWw5OmQehOA0XNfgcgrTrwEJZJJJzE83ovtTQx6JRG";
                break;
            case AppType.BingHomepage:
                apiKey = "QMmdky7jrTlM4mWJmMYQTi71lp678KyZJBKHCAyKCg";
                break;
            case AppType.SharedWidgets:
                apiKey = "hFWgtnoLG1BXruRRhEVJo5ACP8414XUG0Ric6JbyND";
                break;
            case AppType.NewsAndInterests:
            case AppType.WindowsNewsPlus:
                apiKey = "AEbyVYqTQAPvg4lWwMSFHaLkDzl3weRKKIDDInLQUj";
                break;
            default:
                apiKey = "0QfOX3Vn51YCzitbLaRkTTBadtWpgTN8NZLW0C1SEM";
        }
        return apiKey;
    }

    /**
     * Get the UserMuid from the muid cookie
     */
    private getUserMuid(): string {
        let muid: string;
        const muidCookie: string = getMuidCookie();
        if (isString(muidCookie)) {
            muid = "m-" + muidCookie;
        }
        return muid;
    }

    /**
     * Get config service base url
     * @param isMultiConfigRetrievalEnabled if multiple config retrieval is supported.
     * @param configServiceOverride url that is used to override config service url. This value is externally configured from SSR environment.
     * @returns {string} - config service base url.
     */
    private getConfigServiceBaseUrl(isMultiConfigRetrievalEnabled = true, configServiceOverride = null): string {
        const relativePath = this.isGitConfigs
            ? "/resolver/api/resolve/v3/config/"
            : `/resolver/api/resolve/${isMultiConfigRetrievalEnabled ? "v2/" : ""}configindex/`;

        // When doing server side rendering the ClusterRegionUrl env variable should be set, use this to avoid calls to CRS going outside of the cluster.
        if (configServiceOverride) {
            return configServiceOverride + relativePath;
        }

        // int and localhost to use int.msn.com or int.msn.cn for CRS.
        if (process && process.env && process.env.WEBPACK_DEV_SERVER) {
            return (`https://int.msn.${ this.IsCnDomain ? "cn": "com"}`) + relativePath;
        }

        // For Widgets external partners, use CRS domain given by PCS => client settings => servicesEndpoints => crs => domain
        // Use default as www.msn.com or www.msn.cn when servicesEndpoints.crs.domain doesn't have it.
        if (canUseDOM() && (this.renderWidgets() || this.appType === AppType.SharedWidgets || this.appType === AppType.BingHomepage)) {
            return (this.crsDomain || `https://www.msn.${ this.IsCnDomain ? "cn": "com"}`) + relativePath;
        }

        return getLocationOrigin() ? getLocationOrigin() + relativePath : null;
    }

    /**
     * Function checks if ConfigService should return multiple configs
     * @returns true if supported else false.
     */
    private useMultiConfigsRetrieval(): boolean {
        const urlQuery = getLocationSearch();
        if (urlQuery) {
            const param = UrlUtility.getQueryParameterByName("isMultiConfigRetrievalEnabled", urlQuery);
            if (param) {
                return param.toLocaleLowerCase() === "true";
            }
        }

        // PCS flag to turn on/off multi config retrieval.
        if (this.featureFlags
            && this.featureFlags.isMultiConfigRetrievalEnabled) {
            return this.featureFlags.isMultiConfigRetrievalEnabled === "true";
        }

        return true;
    }

    /**
     * Function checks if Git configs is enabled
     * @returns true if supported else false.
     */
    private useGitConfigs(): boolean {
        const urlQuery = getLocationSearch();
        if (urlQuery) {
            const param = UrlUtility.getQueryParameterByName("isGitConfig", urlQuery);
            if (param) {
                return param.toLocaleLowerCase() === "true";
            }
        }

        const flights = this.currentRequestTargetScope && this.currentRequestTargetScope.pageExperiments;
        return shouldUseGitConfigs(this.crsVersion, flights, this.configIndexDocId, this.appType);
    }

    /**
     * Function checks if we are rendering individual experiences (widgets)
     * @returns true if QS is set else false.
     */
    private renderWidgets(): boolean {
        const urlQuery = getLocationSearch();
        const renderWidgetQS = "renderWidget";
        if (urlQuery) {
            const param = UrlUtility.getQueryParameterByName(renderWidgetQS, urlQuery);
            if (param) {
                return param.toLocaleLowerCase() === "true";
            }
        }

        return false;
    }

    /**
     * Get the auth cookie name based on the app type of the page
     * @returns {AuthCookieName} - the name of the auth cookie to use
     */
    private getAuthCookieName(): AuthCookieName {
        const clientSettings = headData && headData.ClientSettings;
        const appType: AppType = (clientSettings.apptype || "") as AppType;
        let authCookieName: AuthCookieName;
        switch (appType) {
            case AppType.Windows:
                authCookieName = headData.ClientSettings.pagetype === PageType.Windows ? AuthCookieName.Anon : AuthCookieName.AppAnon;
                break;
            case AppType.FirstParty:
            case AppType.Hub:
            case AppType.MicrosoftNews:
            case AppType.Views:
            case AppType.Community:
            case AppType.Weather:
            case AppType.Finance:
            case AppType.HomePage:
            case AppType.Hybrid:
                authCookieName = AuthCookieName.Anon;
                break;
            case AppType.Edge:
            case AppType.EdgeChromium:
            case AppType.MMX:
            case AppType.Office:
            default:
                authCookieName = AuthCookieName.AppAnon;
        }

        return authCookieName;
    }

    /**
     * Sets the AccountInfoPromise for the current user.
     * If unable to await, ResolvedAccountInfo is also set when the promise is resolved.
     */
    private setAccountInfoPromise(): void {
        this.ResolvedAccountInfo = null;
        this.AccountInfoPromise = new Promise((resolve) => {
            if (!canUseDOM() || !window.chrome || !window.chrome.authPrivate || !window.chrome.authPrivate.getPrimaryAccountInfo) {
                return resolve(null);
            }

            const markerName = "Auth.GetPrimaryAccountInfo";
            const startTime = Math.round(performance.now());

            window.chrome.authPrivate.getPrimaryAccountInfo((accountInfo) => {
                const endTime = Math.round(performance.now());
                const pageTimings = getPageTimings();
                pageTimings[markerName] = endTime - startTime;

                this.ResolvedAccountInfo = accountInfo;
                return resolve(accountInfo);
            });
        });

        // Start the promise asynchronously.
        this.AccountInfoPromise;
    }

    /**
     * Determines the content link host to use.
     * @returns The content link host.
     */
    private createContentLinkHost(): string {
        const topLevelDomain = this.currentMarket === Market.ZHCN ? "cn" : "com";
        return "www.msn." + topLevelDomain;
    }

    /**
     * Reformats the given guid without dashes to a guid string with dashes.
     * @param {string} guid - The guid without dashes.
     * @returns {string} - The reformatted GUI with dashes.
     */
    private tryReformatGuid(guid: string): string {
        if (guid && guid.length === 32) {
            // use a regex to extract the specific substrings to reformat the guid
            const matches = guid.match(/(\w{8})(\w{4})(\w{4})(\w{4})(\w{12})/i);
            if (matches) {
                // join all the captured groups from 1-5 to format the final guid string
                guid = matches.slice(1).join("-");
            }
        }

        return guid;
    }

    /**
     * Sets target scope for current request.
     */
    private setTargetScope(): void {
        if (!headData || !headData.ClientSettings) {
            return;
        }
        // If user is signed in and "child" Cookie exists - set Kids audience mode, otherwise use PCS provided Audience mode.
        let audienceMode: AudienceModeType;
        if (getCookie(appAnonCookieExistsCookieName) && getCookie(childCookieName)) {
            audienceMode = AudienceModeType.Kids;
        } else if (headData.ClientSettings.audienceMode === AudienceModeType.Enterprise && this.getEnterpriseOverride()) {
            audienceMode = AudienceModeType.Adult;
        } else {
            audienceMode = headData.ClientSettings.audienceMode || AudienceModeType.Adult;
        }

        const { browser, deviceFormFactor, domain, locale, ocid, os, pagetype } = headData.ClientSettings;

        this.currentRequestTargetScope = {
            audienceMode: audienceMode,
            browser: browser,
            deviceFormFactor: deviceFormFactor,
            domain: domain,
            locale: locale,
            ocid,
            os: os,
            platform: PlatformType.Web,
            pageType: pagetype
        };

        // if locale is not defined in client settings, try reading it from headData for backward compatibility
        if (!this.currentRequestTargetScope.locale && Utility.stringHasData(headData.Locale)) {
            const locale = headData.Locale.split("-");
            if (locale.length > 1) {
                this.currentRequestTargetScope.locale = {
                    language: locale[0],
                    // Some spartan locale are in format "ha-latn-ng". Here the market is "ng" and language is "ha"
                    market: locale[locale.length - 1]
                };
            }
        }

        if (Utility.stringHasData(headData.CurrentFlights)) {
            this.currentRequestTargetScope.pageExperiments = headData.CurrentFlights.split(",");
        }

        this.addWpoFlights();
    }

    /**
     * Get WpoModel from WpoData that we got from local storage given the specific WPO
     * key
     */
    private getWpoModel(wpoData: string): WpoModel {
        let model: WpoModel;
        if (wpoData != null) {
            model = tryParseJson(wpoData);
            if (model != null) {
                <WpoModel>model;
            }
        }
        return model;
    }

    /**
     * Adds WPO flights to the current request target scope pageExperiments.
     */
    private addWpoFlights(): void {
        if (!this.FeatureFlags || !this.FeatureFlags.wpoEnabled) {
            return;
        }
        const WPOModel = this.getWpoModel(this.wpoData);
        const flightsCached: string[] = getWpoFlights(WPOModel);

        if (!flightsCached) {
            return;
        }

        const lowerCaseFlightsCached = flightsCached.map(flight => flight.toLowerCase());
        const currentPageExperiments = this.currentRequestTargetScope.pageExperiments || [];
        this.currentRequestTargetScope.pageExperiments = currentPageExperiments.concat(lowerCaseFlightsCached);
    }

    /**
     * Adds WPO flights to the current request target scope pageExperiments.
     */
    private addHubWindowsFlight(locationHref: string): void {
        if (this.isGitConfigs && this.appType === AppType.Hub && isWindowsScenario(locationHref)) {
            const currentPageExperiments = this.currentRequestTargetScope.pageExperiments || [];
            this.currentRequestTargetScope.pageExperiments = currentPageExperiments.concat("prg-iswin-hub");
        }
    }

    private getEnterpriseOverride(): boolean {
        return !!getLocalStorage().getObject(EnterpriseToConsumerOverrideKey);
    }
}

export const getAppEnvironment = AppEnvironment.getInstance;

// appEnvironment can be reinitialized for server side rendering through resetAppEnvironment.
export let appEnvironment = AppEnvironment.getInstance();

export const resetAppEnvironment = AppEnvironment.resetInstance;